import {
  CreateReceiptPayloadDto,
  CreateReceiptPayloadDtoReceiptTypeEnum,
} from 'libs/pinnacle-sdk';
import * as yup from 'yup';
import { TFunction } from 'i18next';
import dayjs from 'dayjs';
import { ContractAllocationRow } from 'components/features/contract-allocation-table/types';
import { FIXED_SETTLEMENT_EXCHANGE_RATE_CURRENCIES } from 'components/features/contract-allocation-table/constants';
import { SettlementExchangeRateItem } from 'components/features/settlement-exchange-rate-table';

export const getValidationSchema = (t: TFunction) =>
  yup.object().shape({
    transactionDate: yup
      .date()
      .required(
        t('validation.required', { field: t('cashReceipts.transactionDate') })
      ),
    receiptType: yup
      .string()
      .oneOf(Object.values(CreateReceiptPayloadDtoReceiptTypeEnum))
      .required(
        t('validation.required', { field: t('cashReceipts.receiptType') })
      ),
    bankAccountNumber: yup
      .string()
      .required(
        t('validation.required', { field: t('cashReceipts.bankAccount') })
      ),
    amount: yup
      .number()
      .required(t('validation.required', { field: t('cashReceipts.amount') }))
      .typeError(t('validation.numeric', { field: t('cashReceipts.amount') }))
      .test(
        'not-zero',
        t('general.notZero', { field: t('cashReceipts.amount') }),
        (value) => value !== 0
      ),
    bankFee: yup
      .number()
      .nullable()
      .typeError(t('validation.numeric', { field: t('cashReceipts.bankFee') }))
      .min(0, t('general.nonNegative', { field: t('cashReceipts.bankFee') })),
    sourceBrokerId: yup
      .string()
      .required(
        t('validation.required', { field: t('cashReceipts.sourceBroker') })
      ),
    sourceBrokerReference: yup.string().optional(),
    fileIds: yup.array().of(yup.string()).optional(),
    comment: yup.string().optional(),
  });

export const initialValues = {
  receiptNumber: undefined,
  transactionDate: dayjs(),
  receiptType: undefined,
  bankAccountNumber: undefined,
  currency: undefined,
  amount: undefined,
  bankFee: 0,
  sourceBrokerId: undefined,
  sourceBrokerReference: undefined,
  fileIds: [],
  comment: undefined,
};

export const fields = Object.keys(
  initialValues
) as (keyof CreateReceiptPayloadDto)[];

export const transformContractAllocationRow =
  (
    bankAccountCurrency: string,
    settlementRateByOriginalCurrency: Record<string, SettlementExchangeRateItem>
  ) =>
  (row: ContractAllocationRow) => {
    const settlementExchangeRateOfTheOriginalCurrency =
      settlementRateByOriginalCurrency?.[row.originalCurrency ?? '']
        ?.settlementExchangeRate;
    const settlementExchangeRate =
      FIXED_SETTLEMENT_EXCHANGE_RATE_CURRENCIES.includes(
        row.originalCurrency || ''
      ) && row.originalCurrency === bankAccountCurrency
        ? 1
        : settlementExchangeRateOfTheOriginalCurrency;

    const bookedExchangeRate = row.bookedExchangeRate
      ? row.bookedExchangeRate
      : FIXED_SETTLEMENT_EXCHANGE_RATE_CURRENCIES.includes(
            row.originalCurrency || ''
          ) && row.originalCurrency === bankAccountCurrency
        ? 1
        : undefined;
    return {
      ...row,
      bookedExchangeRate,
      settlementExchangeRate,
      paidAmount: settlementExchangeRate
        ? settlementExchangeRate * row.originalNetAmountDueBroker
        : undefined,
      forexGainLoss: settlementExchangeRate
        ? (settlementExchangeRate - (bookedExchangeRate || 0)) *
          row.originalNetAmountDueBroker
        : undefined,
      forexPercentage: settlementExchangeRate
        ? ((settlementExchangeRate - (bookedExchangeRate || 0)) /
            settlementExchangeRate) *
          100
        : undefined,
      settlementCurrency: bankAccountCurrency,
    };
  };
